/**
 * @fileoverview This file provides file watching and diff tracking using chokidar for various development modes. It defines the `watchDirectory` function, which monitors a directory and processes file changes using `DiffCollector` and `OnDiffReceiver` implementations like `LogOnDiff`, `CommentOnDiff`, `RewriteOnDiff`, `SuggestOnDiff`, `AutocompleteV9`, and `MulticompleteV4`. It also uses `TypescriptLanguageServer` for TypeScript-aware operations.
 */

import chokidar from 'chokidar'
import { createWriteStream, readFileSync } from 'fs'
import { Model } from '@iterator/shared-node/ai/llm_types'
import { AutocompleteV9 } from './completion/autocomplete_v9'
import { RewriteOnDiff, SuggestOnDiff } from './completion/multicomplete'
import { TypescriptLanguageServer } from './context_ts_language'
import { AwolfIgnorer } from './awolf_ignore'
import { debug, error, info } from '@iterator/shared-node/util/logging'
import {
  BaseLlmOnDiff,
  diffPathToChangeString,
  OnDiffReceiver,
  PathDiff,
  printOnDiff,
} from './on_diff'
import { AWOLF_DIRECTORY_NAME } from './awolf_dir'
import Diff = require('diff')
import debounce = require('debounce')
import { MulticompleteV4 } from './completion/multicomplete_v4'
import { requireSettings } from './settings'
import { ClaudeHaikuModel } from './ai/models'
import { Gpt4oModel } from './ai/models'

export type WatchMode = 'suggest' | 'comment' | 'rewrite' | 'log' | 'multicomplete' | 'autocomplete'

export async function watchDirectory(
  directory: string,
  mode: WatchMode,
  delay: number = 5000,
): Promise<void> {
  console.clear()
  info(`Watching directory ${directory} (mode=${mode}, delay=${delay}ms)`)

  const tsLanguage = new TypescriptLanguageServer(directory)
  await tsLanguage.start()
  const ignorer = new AwolfIgnorer(directory)
  const watcher = chokidar.watch(directory, {
    ignored: (path) => ignorer.shouldSkip(path),
    persistent: true,
  })

  const onDiffs: OnDiffReceiver[] = (() => {
    switch (mode) {
      case 'multicomplete':
        return [new MulticompleteV4()]
      case 'suggest':
        return [new SuggestOnDiff(ClaudeHaikuModel), new LogOnDiff()]
      case 'rewrite':
        return [new RewriteOnDiff(Gpt4oModel), new LogOnDiff()]
      case 'comment':
        return [new CommentOnDiff(ClaudeHaikuModel), new LogOnDiff()]
      case 'log':
        return [new LogOnDiff(true)]
      case 'autocomplete':
        const logDiff = new LogOnDiff(false, `${AWOLF_DIRECTORY_NAME}/watch_autocomplete.jsonl`)
        return [new AutocompleteV9(tsLanguage, false), logDiff]
      default:
        throw new Error(`Unsupported mode: ${mode}`)
    }
  })()

  const diffCollector = new DiffCollector(onDiffs, delay)
  let isReady = false
  watcher
    .on('add', (path) => {
      const content = readFileSync(path, 'utf8')
      tsLanguage.onFileAdd(path, content)
      if (isReady) {
        diffCollector.onFileAdd(path, content)
      } else {
        debug(`Scanned file ${path}`)
      }
    })
    .on('change', (path) => {
      const content = readFileSync(path, 'utf8')
      tsLanguage.onFileChange(path, content)
      diffCollector.onFileChange(path, content)
    })
    .on('unlink', (path) => {
      tsLanguage.onFileRemove(path)
      diffCollector.onFileRemove(path)
    })
    .on('error', (e) => error(`Watcher error: ${e}`))
    .on('ready', () => {
      isReady = true
      info('Initial scan complete. Ready for changes')
    })
    .on('raw', (event, path, details) => {
      debug('Raw event info:', event, path, details)
    })
}

/**
 * Log diffs to a jsonl file where each line has the format:
 * { timestamp: number, path: string, before: string, changes: Diff.Change[] }
 */
class LogOnDiff implements OnDiffReceiver {
  mostRecentResult: string | undefined = undefined

  constructor(
    private shouldPrintDiff: boolean = false,
    private logPath: string = `${AWOLF_DIRECTORY_NAME}/watch.jsonl`,
  ) {}

  async onDiff(diff: PathDiff) {
    if (this.shouldPrintDiff) {
      await printOnDiff(diff)
    }
    const logLines = [
      {
        timestamp: Date.now(),
        path: diff.path,
        before: diff.before,
        changes: diff.changes,
      },
    ]
    const stream = createWriteStream(this.logPath, { flags: 'a' })
    logLines.forEach((l) => stream.write(JSON.stringify(l) + '\n'))
    stream.end()
  }

  async logSuggestion() {
    const stream = createWriteStream(this.logPath, { flags: 'a' })
    stream.write(JSON.stringify({ timestamp: Date.now(), suggestion: true }) + '\n')
    stream.end()
  }
}

/**
 * Simply describe what the programmer is doing in each change.
 */
class CommentOnDiff extends BaseLlmOnDiff {
  constructor(model: Model) {
    super(model, { shouldPrintDiff: true, shouldPrintStream: true })
    requireSettings([...model.getApi().requiredKeys])
  }

  getSystemPrompt(): string {
    return 'You are an AI coding assistant. You are provided with a series of code changes and source files. Your job is to comment on the changes.'
  }

  getTexts(_diff: PathDiff): string[] {
    return []
  }

  getUserPrompt(diff: PathDiff): string {
    return `<recent_change>\n${diffPathToChangeString(diff)}\n</recent_change>

<instructions>
Explain what the programmer did in <recent_change> in 1 brief sentence. Use imperative language.
</instructions>`
  }
}

// TODO handle multi-file diffs that happen at the same time
class DiffCollector {
  private pathToContent: Record<string, string> = {}
  private pathToNewContent: Record<string, string> = {}
  private emitNewContent: () => Promise<void> | undefined

  constructor(
    private receivers: OnDiffReceiver[],
    delay: number = 5000,
  ) {
    this.emitNewContent = debounce(async () => {
      for (const path in this.pathToNewContent) {
        if (this.pathToNewContent[path] !== this.pathToContent[path]) {
          await this.emitChangesForPath(path)
        }
        if (this.pathToNewContent[path]) {
          this.pathToContent[path] = this.pathToNewContent[path]
        }
      }
      this.pathToNewContent = {}
    }, delay)
  }

  async emitChangesForPath(path: string) {
    if (this.pathToNewContent[path] && this.pathToContent[path]) {
      const changes = Diff.diffLines(this.pathToContent[path], this.pathToNewContent[path])
      const diff: PathDiff = {
        path,
        changes,
        before: this.pathToContent[path],
        after: this.pathToNewContent[path],
      }
      this.receivers.forEach((receiver) => receiver.onDiff(diff))
    }
  }

  onFileChange(path: string, content?: string) {
    const currentContent = content || readFileSync(path, 'utf8')

    if (this.pathToContent[path]) {
      this.pathToNewContent[path] = currentContent
    } else {
      this.pathToContent[path] = currentContent
    }

    this.emitNewContent()
  }

  onFileAdd(path: string, content?: string) {
    const currentContent = content || readFileSync(path, 'utf8')
    this.pathToContent[path] = currentContent
  }

  onFileRemove(path: string) {
    delete this.pathToContent[path]
  }
}
